import { CdkStepper, CdkStepperNext, CdkStepperPrevious } from '@angular/cdk/stepper';
import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { AppState } from 'app/state/app.state';
import * as fromRoot from 'app/state/selectors/product-wizard.selectors';
import { FormaButton, FormaButtonGroup, FormaCard, FormaStepButton } from 'forma';
import { filter, Subscription, take } from 'rxjs';
import { InsightCardModule } from '../../ui/card';
import { UIKitButtonModule } from '../button';
import { UIKitIconModule } from '../icon';
import { UIKitSquareButtonModule } from '../square-button/square-button.module';

@Component({
  selector: 'uikit-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  providers: [{ provide: CdkStepper, useExisting: UIKitStepper }],
  host: { '[class.has-fixed-footer]': '!integratedStepper()' },
  imports: [
    InsightCardModule,
    NgTemplateOutlet,
    UIKitButtonModule,
    TranslocoPipe,
    UIKitIconModule,
    UIKitSquareButtonModule,
    FormaCard,
    FormaStepButton,
    FormaButton,
    FormaButtonGroup,
    CdkStepperNext,
    CdkStepperPrevious,
  ],
})
export class UIKitStepper extends CdkStepper implements AfterViewInit {
  protected changeDetectorRef: ChangeDetectorRef;

  private store = inject<Store<AppState>>(Store<AppState>);
  private wizardDestroyRef = inject(DestroyRef);
  readonly productId = input<number>(undefined);
  readonly finishButtonText = input<string>(undefined);
  readonly title = input<string>(undefined);
  readonly furthestVisitedStep = input<number>(0);
  integratedStepper = input<boolean>(false);

  finish = output<boolean>();
  save = output<boolean>();
  close = output<boolean>();
  isSubmitting = signal(false);
  scrollContainer: HTMLElement;
  saving = this.store.selectSignal(fromRoot.selectProductWizardSaving());
  unsaved = this.store.selectSignal(fromRoot.selectProductWizardUnsaved());
  isEditMode = this.store.selectSignal(fromRoot.selectProductWizardIsEditMode());
  isPaused = this.store.selectSignal(fromRoot.selectProductWizardIsPaused());
  unsavedSubsription: Subscription;

  constructor() {
    super();
    this.changeDetectorRef = inject(ChangeDetectorRef);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.scrollContainer = document.querySelector('.insight-container');
  }

  onClick(index: number): void {
    this.selectedIndex = index;
  }

  isValidToSubmit(): boolean {
    return this.steps.map((step) => step.completed).reduce((x, y) => x && y, true);
  }
  saveAndFinishAction() {
    if (!this.isValidToSubmit()) return;

    this.isSubmitting.set(true);
    this.save.emit(false);

    if (this.unsavedSubsription) {
      this.unsavedSubsription.unsubscribe();
    }

    this.unsavedSubsription = this.store
      .select((s) => s.surveyWizardContext?.unsaved)
      .pipe(
        filter((us) => !us),
        take(1),
        takeUntilDestroyed(this.wizardDestroyRef),
      )
      .subscribe((_) => {
        this.finish.emit(true);
      });
  }
  finishAction(): void {
    if (this.isValidToSubmit()) {
      this.finish.emit(true);
    }
  }

  cancel(): void {
    this.onClose();
  }

  showToTop(): boolean {
    return this.scrollContainer?.scrollTop > 100;
  }

  scrollToTop(): void {
    this.scrollContainer.scroll({ top: 0, behavior: 'smooth' });
  }

  onSave(): void {
    this.save.emit(true);
  }

  onClose(): void {
    this.close.emit(true);
  }
}
